import React, { useContext, useEffect, useState } from 'react'
import { Formik, Form, FieldArray, Field } from 'formik'
import { toast } from 'react-toastify'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'

import { FiPlus, FiSave, FiTrash } from 'react-icons/fi'
import FormEndereco from '../../../../components/FormEndereco/v2'
import {
    Select, Textbox, Button 
} from '../../../../components/Form'

import Yup from '../../../../services/yup'
import masks from '../../../../util/masks'
import stringUtils from '../../../../util/string'
import formUtils from '../../../../util/form'

import { api, authHeaders } from '../../../../services/api'

import { Container } from './styles'
import { TipContext } from '../../../../contexts/TipContext'

const baseInitialValues = {
    razao_social: '',
    nome_fantasia: '',
    sigla: '',
    cnpj: '',
    tipo: null,
    endereco_id: null,
    cep: '',
    uf: '',
    cidade: '',
    bairro: '',
    logradouro: '',
    numero: '',
    complemento: '',
    representantes: []
}

const validation = Yup.object({
    razao_social: Yup.string().required('Informe a Razão Social.'),
    nome_fantasia: Yup.string().required('Informe o Nome Fantasia.'),
    sigla: Yup.string(),
    cnpj: Yup.string().required('Informe o CNPJ.'),
    tipo: null,
    endereco_id: null,
    cep: Yup.string().required('Informe o CEP.'),
    uf: Yup.object({
        label: Yup.string(),
        value: Yup.string()
    }).required('Informe a UF.'),
    cidade: Yup.object({
        label: Yup.string(),
        value: Yup.string()
    }).required('Informe a cidade.'),
    bairro: Yup.string().required('Informe o bairro.'),
    logradouro: Yup.string().required('Informe o logradouro.'),
    numero: Yup.string(),
    complemento: Yup.string(),
    representantes: Yup.array().of(
        Yup.object({
            nome: Yup.string().required('Informe o nome do representante.'),
            email: Yup.string().required('Informe o e-mail do representante.')
        })
    ).min(1, 'Inclua pelo menos um representante.').required('Inclua pelo menos um representante.')
})

export default function ({ instituicao, reload, edicao }) {
    const { tips } = useContext(TipContext)

    const [tiposInstituicao, setTiposInstituicao] = useState(null)
    const [initialValues] = useState(instituicao?.id ? {
        ...instituicao,
        ...(instituicao.endereco || {}),
        logradouro: instituicao.endereco?.rua || '',
        uf: instituicao.endereco?.uf ? { label: instituicao.endereco.uf, value: instituicao.endereco.uf } : null,
        cidade: instituicao.endereco?.cidade ? { label: instituicao.endereco.cidade, value: instituicao.endereco.cidade } : null,
        tipo: instituicao.tipo_instituicao ? {
            label: instituicao.tipo_instituicao.descricao,
            value: instituicao.tipo_instituicao.id
        } : null,
        representantes: instituicao.representantes.map(representante => ({
            nome: representante.pessoa.nome,
            email: representante.pessoa.contato[0].contato,
            id: representante.id,
            pessoa_id: representante.pessoa.id
        }))
    } : baseInitialValues)

    async function loadInsituicoes() {
        const response = await api.get('tipo_instituicao', authHeaders())

        setTiposInstituicao(response.map(tipo => ({
            label: stringUtils.capitalize(tipo.descricao),
            value: tipo.id
        })))
    }
    
    function verificarExclusaoHabilitada(values) {
        return values.representantes.length > 1
    }

    useEffect(() => {
        loadInsituicoes()
    }, [])

    async function handleSubmit(values) {
        values = formUtils.extractFormValues(values)

        values.endereco = {
            cep: values.cep,
            uf: values.uf,
            cidade: values.cidade,
            bairro: values.bairro,
            rua: values.logradouro,
            numero: values.numero,
            complemento: values.complemento
        }

        delete values.cep
        delete values.uf
        delete values.cidade
        delete values.bairro
        delete values.logradouro
        delete values.numero
        delete values.complemento

        try {
            if(edicao) {
                await api.put(`instituicao/${instituicao.id}`, values, authHeaders())

                toast.success('Instituição alterada com sucesso.')
            } else {
                await api.post('instituicao', values, authHeaders())

                toast.success('Instituição cadastrada com sucesso.')
            }

            reload()
        } catch(e) {
            if(e.code === 409) {
                toast.warn(e.msg)
            } else {
                toast.error(e.msg)
            }
        }
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validation}
        >
            {({ setFieldValue, isSubmitting, values, errors }) => {
                return (
                    <Form>
                        <Container>
                            <p style={{ margin: 8, gridArea: 'tips' }}>{tips?.formulario}</p>
    
                            <Textbox
                                label="Razão social"
                                name="razao_social"
                                containerStyle={{ gridArea: 'f1' }}
                            />
    
                            <Textbox
                                label="Nome fantasia"
                                name="nome_fantasia"
                                containerStyle={{ gridArea: 'f2' }}
                            />
    
                            <Textbox
                                label="Sigla"
                                name="sigla"
                                containerStyle={{ gridArea: 'f3' }}
                            />
    
                            <Textbox
                                label="CNPJ"
                                name="cnpj"
                                mask={masks.cnpj}
                                containerStyle={{ gridArea: 'f4' }}
                            />
    
                            <Select
                                label="Tipo"
                                name="tipo"
                                options={tiposInstituicao}
                                onChange={(selected, meta) => {
                                    setFieldValue(meta.name, selected)
                                }}
                                containerStyle={{ gridArea: 'f5' }}
                            />
    
                            <input type="hidden" value={instituicao.endereco?.id} name="endereco_id" />
    
                            <div></div>
    
                            <hr className="bg-white" style={{ gridArea: 'line_1' }} />
    
                            <h2 style={{ gridArea: 'titulo_endereco' }}>Endereço</h2>
    
                            <FormEndereco setFieldValue={setFieldValue} />
    
                            <div></div>
                            <div></div>
    
                            <hr className="bg-white" style={{ gridArea: 'line_2' }} />
    
                            <h2 style={{ gridArea: 'titulo_representante' }}>Representante</h2>
    
                            {errors.representantes && typeof errors.representantes === 'string' && (
                                <div className="bg-yellow-500 text-gray-800 w-fit m-2 p-2 rounded-md" style={{ fontSize: 12, gridArea: 'erro_representante' }}>{errors.representantes}</div>
                            )}
    
                            <FieldArray 
                                name="representantes"
                                render={({ push, remove }) => {
                                    return (
                                        <div style={{ gridArea: 'representante' }}>
                                            {values.representantes.map((_, idx) => {
                                                const exclusaoHabilitada = verificarExclusaoHabilitada(values)
    
                                                return (
                                                    <div key={idx} className="flex items-center">
                                                        <Textbox 
                                                            name={`representantes.${idx}.nome`}
                                                            label="Nome"
                                                        />
    
                                                        <Textbox 
                                                            name={`representantes.${idx}.email`}
                                                            label="E-mail"
                                                        />
    
                                                        <FiTrash 
                                                            size={16} 
                                                            className={twMerge(
                                                                classNames(
                                                                    'hover:opacity-80 w-9 mt-5',
                                                                    {
                                                                        'opacity-60': !exclusaoHabilitada
                                                                    }
                                                                )
                                                            )} 
                                                            style={{
                                                                cursor: exclusaoHabilitada ? 'pointer' : 'not-allowed'
                                                            }}
                                                            onClick={() => { 
                                                                if(exclusaoHabilitada) {
                                                                    remove(idx) 
                                                                }
                                                            }} 
                                                        />
    
                                                        <Field type="hidden" name={`representantes.${idx}.id`} />
                                                        <Field type="hidden" name={`representantes.${idx}.pessoa_id`} />
                                                    </div>
                                                ) 
                                            })}
    
                                            <Button
                                                onClick={() => {
                                                    push({
                                                        nome: '',
                                                        email: ''
                                                    })
                                                }}
                                                className="white"
                                            >
                                                Adicionar representante
                                                <FiPlus size={16} />
                                            </Button>
                                        </div>
                                    )
                                }}
                            />
    
                            <div className="button-container">
                                <Button type="submit" className="transparent" loading={isSubmitting}>
                                    {edicao ? 'Salvar alterações' : 'Cadastrar'}
                                    {edicao ? <FiSave size={16} /> : <FiPlus size={16} />}
                                </Button>
                            </div>
                        </Container>
                    </Form>
                )
            }}
        </Formik>
    )
}
